// `PanelTitleRow` — the Progress panels' title layout: left-aligned title, the
// `?` pill on the right, and the help body below when it is open. The pill and
// the body themselves are the shared <InfoPill>/<InfoPillBody> primitives (the
// program composer uses the same pair on a table column header); this file owns
// only the title ROW arrangement. [[feedback_quick_tip_pill_layout]]
import type { ReactNode } from "react";
import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import { InfoPill, InfoPillBody } from "../primitives/InfoPill";
import type { UiTheme } from "../theme";

// Convenience layout: pairs a left-aligned title with the pill on the
// right and the (optional) help body below. Drop-in for a panel's title.
export function PanelTitleRow({
  theme,
  title,
  body,
  period,
  right,
}: {
  theme: UiTheme;
  title: string;
  body: string;
  period?: string;
  right?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const styles = titleStyles(theme);
  return (
    <View>
      <View style={styles.titleRow}>
        <Text
          style={styles.title}
          numberOfLines={1}
          accessibilityRole="header"
        >
          {title}
        </Text>
        <View style={styles.rightCluster}>
          {right}
          {period ? <Text style={styles.period}>{period}</Text> : null}
          <InfoPill
            theme={theme}
            open={open}
            onToggle={() => setOpen((v) => !v)}
          />
        </View>
      </View>
      {open ? <InfoPillBody theme={theme} text={body} /> : null}
    </View>
  );
}

function titleStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    titleRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s2,
    },
    title: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
      flex: 1,
      minWidth: 0,
    },
    rightCluster: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
    },
    period: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
  });
}
